import { useCallback, useContext, useState } from 'react';
import { billingService } from '../components/shared/hooks/AuthService';
import AuthContext from '../components/shared/AuthContext';

/**
 * Starts Stripe Checkout (hosted) or demo upgrade via POST /api/billing/checkout-session.
 */
export function useStartCheckout() {
  const { isAuthenticated } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const startCheckout = useCallback(
    async ({ plan = 'pro', interval = 'monthly', loginRedirect = '/checkout' } = {}) => {
      setError('');
      const params = new URLSearchParams({ plan, interval });
      const checkoutPath = `/checkout?${params.toString()}`;

      if (!isAuthenticated) {
        window.location.assign(`/login?redirect=${encodeURIComponent(checkoutPath)}`);
        return { started: false };
      }

      setLoading(true);
      try {
        const res = await billingService.createCheckout(plan, interval);
        const data = res.data || {};
        if (data.url) {
          window.location.href = data.url;
          return { started: true, mode: data.mode || 'stripe' };
        }
        if (data.redirectUrl) {
          window.location.href = data.redirectUrl;
          return { started: true, mode: data.mode || 'demo' };
        }
        const msg = data.message || 'Checkout could not be started.';
        setError(msg);
        return { started: false, error: msg };
      } catch (err) {
        const msg =
          err.response?.data?.error ||
          err.response?.data?.message ||
          'Payment gateway unavailable. Try again or contact support.';
        setError(msg);
        return { started: false, error: msg };
      } finally {
        setLoading(false);
      }
    },
    [isAuthenticated]
  );

  return { startCheckout, loading, error, clearError: () => setError('') };
}
